package at.theatervogelweide.admin.web.screens.people

import at.theatervogelweide.admin.web.components.AdminImageDetailsDialog
import at.theatervogelweide.admin.contract.ImageDetails
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.IMAGE_EXTENSIONS
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.people.AddressDraft
import at.theatervogelweide.admin.contract.people.PeopleService
import at.theatervogelweide.admin.contract.people.PersonDraft
import at.theatervogelweide.admin.contract.people.PersonEditor
import at.theatervogelweide.admin.contract.people.PersonListEntry
import at.theatervogelweide.admin.contract.people.fullName
import at.theatervogelweide.admin.contract.people.initials
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.Route
import at.theatervogelweide.admin.web.components.AdminAvatar
import at.theatervogelweide.admin.web.components.AdminBadge
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCallout
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminCardFooterSpacer
import at.theatervogelweide.admin.web.components.AdminConfirmDialog
import at.theatervogelweide.admin.web.components.AdminConflictCallout
import at.theatervogelweide.admin.web.components.AdminDataTable
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminSearchField
import at.theatervogelweide.admin.web.components.AdminSplitView
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.BadgeVariant
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.DataTableColumn
import at.theatervogelweide.admin.web.components.EditableImages
import at.theatervogelweide.admin.web.components.LocalPanes
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.SplitController
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.components.editorShown
import at.theatervogelweide.admin.web.rpcCall
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.asList

private val formGrid = Bem("admin-form-grid")
private val listPanel = Bem("admin-list-panel")
private val alert = Bem("admin-alert")
private val fieldset = Bem("admin-fieldset")
private val dataTable = Bem("admin-data-table")
private val photoBlock = Bem("admin-person-photo")
private val address = Bem("admin-address")
private val roles = Bem("admin-role-history")

/** The editor of one person, or of a new one while [loaded] is `null`. */
private class PersonEditorState(loaded: PersonEditor?, val photo: EditableImages) {
    var loaded: PersonEditor? by mutableStateOf(loaded)
    val original: PersonDraft = loaded?.draft ?: PersonDraft()

    /** Everything but the photo, which [photo] holds. */
    var form: PersonDraft by mutableStateOf(original)
    var fieldErrors: List<FieldError> by mutableStateOf(emptyList())
    var error: String? by mutableStateOf(null)
    var conflict: Boolean by mutableStateOf(false)
    var saving: Boolean by mutableStateOf(false)
    var reloading: Boolean by mutableStateOf(false)

    val busy: Boolean get() = saving || reloading

    val isNew: Boolean get() = loaded == null

    /** The latest photo wins; an earlier one stays shown while a new one uploads and if its upload fails. */
    val photoUuid: String? get() = photo.images.lastOrNull()?.uuid

    val draft: PersonDraft get() = form.copy(imageUuid = photoUuid, imageDetails = photo.detailsToSave.filterKeys { it == photoUuid })

    val hasChanges: Boolean
        get() = form.copy(imageUuid = original.imageUuid, imageDetails = original.imageDetails) != original || photo.uploading ||
            photoUuid != original.imageUuid || photo.currentDetails.filterKeys { it == photoUuid } != original.imageDetails

    fun errorFor(name: String): String? = fieldErrors.firstOrNull { it.field == name }?.message

    fun showFailure(failure: AdminError) {
        conflict = failure.code == ErrorCode.CONFLICT
        fieldErrors = failure.fieldErrors
        error = when {
            conflict || failure.fieldErrors.isNotEmpty() -> null
            failure.code == ErrorCode.NOT_FOUND -> "Diese Person gibt es nicht mehr. Bitte wähle eine andere."
            else -> failure.message
        }
    }
}

/**
 * Personen (#23): the table of people with a search beside the editor of the selected person. [selectedId] comes from
 * the address, and [split] changes it; with panes and no selection the first person opens. [startNew] opens the form
 * for a new person (`/personen/neu`). [onOpenAct] opens a production of the role history in Stücke & Termine.
 */
@Composable
fun PeopleScreen(
    rpc: RpcClient,
    api: AdminApi,
    guard: UnsavedChangesGuard,
    split: SplitController,
    selectedId: Long?,
    startNew: Boolean = false,
    onOpenAct: (Long) -> Unit,
) {
    val service = remember(rpc) { rpc.withService<PeopleService>() }
    val scope = rememberCoroutineScope()

    var entries by remember { mutableStateOf<List<PersonListEntry>?>(null) }
    var listError by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var editor by remember { mutableStateOf<PersonEditorState?>(null) }
    var editorError by remember { mutableStateOf<String?>(null) }
    var creating by remember { mutableStateOf(startNew) }
    var confirmDelete by remember { mutableStateOf(false) }

    fun newEditor(loaded: PersonEditor?): PersonEditorState {
        editor?.photo?.dispose()
        return PersonEditorState(loaded, newPhoto(scope, api, loaded?.draft?.imageUuid, loaded?.draft?.imageDetails.orEmpty()))
    }

    suspend fun reloadList() {
        when (val result = rpcCall { service.list() }) {
            is AdminResult.Success -> {
                entries = result.value
                listError = null
            }

            is AdminResult.Failure -> listError = result.error.message
        }
    }

    /** Loads [id] in place of [replacing]; the answer is dropped if another editor opened meanwhile. */
    suspend fun loadEditor(id: Long, replacing: PersonEditorState? = editor) {
        val result = rpcCall { service.load(id) }
        if (editor !== replacing) {
            return
        }
        when (result) {
            is AdminResult.Success -> {
                editor = newEditor(result.value)
                editorError = null
            }

            is AdminResult.Failure -> {
                editor?.photo?.dispose()
                editor = null
                editorError = if (result.error.code == ErrorCode.NOT_FOUND) "Diese Person gibt es nicht mehr." else result.error.message
            }
        }
    }

    LaunchedEffect(service) { reloadList() }

    // the form for a new person follows the address: /personen/neu opens it, leaving it for /personen closes it
    LaunchedEffect(startNew) {
        when {
            startNew && editor?.isNew != true -> {
                creating = true
                editorError = null
                editor = newEditor(null)
            }

            !startNew && creating && selectedId == null -> {
                editor?.photo?.dispose()
                editor = null
                creating = false
            }
        }
    }

    // leaving the screen frees the local preview of a photo that is still uploading
    DisposableEffect(Unit) {
        onDispose { editor?.photo?.dispose() }
    }

    LaunchedEffect(service, selectedId) {
        if (selectedId != null && editor?.loaded?.id != selectedId) {
            creating = false
            loadEditor(selectedId)
        }
    }

    // with panes and without a selection, the first person opens
    val panes = LocalPanes.current
    LaunchedEffect(entries, selectedId, creating, panes) {
        val first = entries?.firstOrNull()
        if (panes && selectedId == null && !creating && first != null) {
            split.show(first.id)
        }
    }

    // back on the list alone, the editor and its changes are gone
    val editorVisible = editorShown(selectedId, startNew)
    LaunchedEffect(editorVisible) {
        if (!editorVisible) {
            editor?.photo?.dispose()
            editor = null
            editorError = null
            creating = false
        }
    }

    val state = editor.takeIf { editorVisible }
    val hasChanges = state?.hasChanges == true
    ReportUnsavedChanges(guard, hasChanges)

    fun select(id: Long) {
        if (id == selectedId && !creating) {
            return
        }
        guard.proceed {
            creating = false
            if (editor?.loaded?.id != id) {
                editor?.photo?.dispose()
                editor = null
            }
            split.open(id)
        }
    }

    fun startNew() {
        guard.proceed {
            creating = true
            editorError = null
            editor = newEditor(null)
            split.openNew()
        }
    }

    fun save() {
        val current = editor ?: return
        val draft = current.draft
        val errors = draft.validate()
        current.fieldErrors = errors
        current.error = null
        if (errors.isNotEmpty() || current.saving) {
            return
        }

        current.saving = true
        scope.launch {
            val loaded = current.loaded
            val result = rpcCall { if (loaded == null) service.create(draft) else service.save(loaded.versionedId, draft) }
            current.saving = false
            // the person may have switched to another one meanwhile; then only the list shows the result
            if (editor !== current) {
                reloadList()
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    editor = newEditor(result.value)
                    creating = false
                    split.show(result.value.id)
                    reloadList()
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    fun discard() {
        val current = editor ?: return
        val loaded = current.loaded
        if (loaded == null) {
            current.photo.dispose()
            editor = null
            creating = false
            split.backToList()
        } else {
            editor = newEditor(loaded)
        }
    }

    fun reloadAfterConflict() {
        val current = editor ?: return
        val id = current.loaded?.id ?: return
        if (current.reloading) {
            return
        }
        current.reloading = true
        scope.launch {
            try {
                loadEditor(id, replacing = current)
            } finally {
                current.reloading = false
            }
            reloadList()
        }
    }

    fun delete() {
        confirmDelete = false
        val current = editor ?: return
        val loaded = current.loaded ?: return
        scope.launch {
            val result = rpcCall { service.delete(loaded.versionedId, loaded.draft.addressVersion) }
            if (editor !== current) {
                reloadList()
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    current.photo.dispose()
                    editor = null
                    // gone from the list before the selection moves on, so the first remaining person opens
                    entries = entries?.filterNot { it.id == loaded.id }
                    split.backToList()
                    reloadList()
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    AdminPageHeader("Personen", unsavedChanges = hasChanges) {
        // the search acts on the list only, which the narrow editor view leaves out
        if (panes || !editorVisible) {
            AdminSearchField(
                id = "personen-suche",
                label = "Personen suchen",
                value = query,
                onValueChange = { query = it },
                placeholder = "Name, E-Mail, Rolle …",
            )
        }
        AdminButton(variant = ButtonVariant.PRIMARY, onClick = ::startNew) { Text("+ Person") }
    }

    val shown = entries.orEmpty().filter { it.matches(query) }

    AdminSplitView(
        controller = split,
        selectedId = selectedId,
        new = startNew,
        name = "Personen",
        listLabel = "Liste der Personen",
        table = true,
        list = {
            listError?.let {
                P({
                    classes(listPanel.element("message").className, alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            // announced while typing, so the search says what it found
            P({
                classes("admin-visually-hidden")
                attr("role", "status")
            }) {
                if (entries != null && query.isNotBlank()) {
                    Text(if (shown.size == 1) "1 Person gefunden" else "${shown.size} Personen gefunden")
                }
            }

            when {
                entries == null -> P({ classes(listPanel.element("message").className) }) { Text("Lade …") }
                entries!!.isEmpty() -> P({ classes(listPanel.element("message").className) }) {
                    Text("Noch keine Personen. Lege mit „+ Person“ die erste an.")
                }

                shown.isEmpty() -> P({ classes(listPanel.element("message").className) }) {
                    Text("Keine Person passt zur Suche „${query.trim()}“.")
                }

                else -> AdminDataTable(
                    rows = shown,
                    key = { it.id },
                    caption = "Personen",
                    selected = if (creating) null else selectedId,
                    recent = split.recent,
                    inCard = true,
                    columns = listOf(
                        DataTableColumn("Name") { person -> PersonCell(person, api, selected = !creating && person.id == selectedId, onSelect = ::select) },
                        DataTableColumn("Kontakt") { person -> Text(person.email ?: "–") },
                        DataTableColumn("Stücke", number = true) { person -> Text(if (person.actCount == 0) "–" else person.actCount.toString()) },
                        DataTableColumn("Zuletzt", number = true) { person -> Text(person.lastYear?.toString() ?: "–") },
                    ),
                )
            }
        },
        detail = {
            when {
                state != null -> PersonEditorCard(
                    state = state,
                    api = api,
                    onOpenAct = onOpenAct,
                    onSave = ::save,
                    onDiscard = ::discard,
                    onDelete = { confirmDelete = true },
                    onReload = ::reloadAfterConflict,
                )

                editorError != null -> P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(editorError!!) }
            }
        },
    )

    val loaded = state?.loaded
    AdminConfirmDialog(
        open = confirmDelete,
        id = "person-loeschen",
        title = "Person löschen?",
        message = buildString {
            append("„${loaded?.draft?.let { fullName(it.firstName, it.lastName) }.orEmpty()}“ wird mit Kontaktdaten, Adresse und Foto gelöscht")
            if (loaded?.boardFunctions?.isNotEmpty() == true) {
                append(" und verschwindet aus dem Vorstand")
            }
            append(". Das lässt sich nicht rückgängig machen.")
        },
        confirmLabel = "Löschen",
        cancelLabel = "Behalten",
        onConfirm = ::delete,
        onCancel = { confirmDelete = false },
        danger = true,
    )
}

private fun newPhoto(scope: CoroutineScope, api: AdminApi, uuid: String?, details: Map<String, ImageDetails>) =
    EditableImages(scope, { file -> api.uploadImage(ImageCategory.PERSONS, file) }, listOfNotNull(uuid), details = details)

/** The name cell: the button that selects the row, with the photo and the board badge. */
@Composable
private fun PersonCell(person: PersonListEntry, api: AdminApi, selected: Boolean, onSelect: (Long) -> Unit) {
    Button({
        type(ButtonType.Button)
        classes(dataTable.element("select").className)
        attr("aria-pressed", selected.toString())
        onClick { onSelect(person.id) }
    }) {
        AdminAvatar(initials(person.firstName, person.lastName), person.imageUuid?.let { api.imageUrl(ImageCategory.PERSONS, it) })
        Span({ classes(dataTable.element("title").className) }) { Text(person.name) }
        if (person.boardFunctions.isNotEmpty()) {
            AdminBadge("Vorstand", BadgeVariant.BOARD)
        }
    }
}

@Composable
private fun PersonEditorCard(
    state: PersonEditorState,
    api: AdminApi,
    onOpenAct: (Long) -> Unit,
    onSave: () -> Unit,
    onDiscard: () -> Unit,
    onDelete: () -> Unit,
    onReload: () -> Unit,
) {
    val form = state.form
    val loaded = state.loaded
    val deleteBlocker = loaded?.deleteBlocker

    fun update(change: PersonDraft.() -> PersonDraft) {
        state.form = state.form.change()
    }

    fun updateAddress(change: AddressDraft.() -> AddressDraft) {
        update { copy(address = (address ?: AddressDraft()).change()) }
    }

    Form(attrs = {
        attr("novalidate", "")
        onSubmit { event ->
            event.preventDefault()
            onSave()
        }
    }) {
        AdminCard(
            title = if (state.isNew) "Neue Person" else fullName(state.original.firstName, state.original.lastName),
            id = "person",
            header = {
                if (loaded?.boardFunctions?.isNotEmpty() == true) {
                    AdminBadge("Vorstand", BadgeVariant.BOARD)
                }
            },
            footer = if (state.isNew) {
                null
            } else {
                {
                    AdminButton(variant = ButtonVariant.DANGER, disabled = state.busy || deleteBlocker != null, onClick = onDelete) {
                        Text("Löschen")
                    }
                    AdminCardFooterSpacer()
                }
            },
            saveActions = {
                AdminButton(disabled = state.busy || (!state.hasChanges && !state.isNew), onClick = onDiscard) { Text("Verwerfen") }
                AdminButton(
                    variant = ButtonVariant.PRIMARY,
                    type = ButtonType.Submit,
                    disabled = state.busy || state.photo.uploading || (!state.hasChanges && !state.isNew),
                ) { Text(if (state.saving) "Speichert …" else "Speichern") }
            },
        ) {
            if (state.conflict) {
                AdminConflictCallout(onReload = onReload)
            }
            state.error?.let {
                P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            Fieldset({
                classes(fieldset.block)
                if (state.busy) {
                    attr("disabled", "")
                }
            }) {
                PersonPhoto(state, api)

                Div({ classes(formGrid.block) }) {
                    AdminTextField(
                        id = "person-${PersonDraft.FIRST_NAME}",
                        label = "Vorname",
                        value = form.firstName,
                        onValueChange = { update { copy(firstName = it) } },
                        error = state.errorFor(PersonDraft.FIRST_NAME),
                        autocomplete = "off",
                    )
                    AdminTextField(
                        id = "person-${PersonDraft.LAST_NAME}",
                        label = "Nachname",
                        value = form.lastName,
                        onValueChange = { update { copy(lastName = it) } },
                        error = state.errorFor(PersonDraft.LAST_NAME),
                        autocomplete = "off",
                    )
                    Div({ classes(formGrid.element("item").className, formGrid.element("item").modifier("span-2")) }) {
                        AdminTextField(
                            id = "person-${PersonDraft.EMAIL}",
                            label = "E-Mail",
                            value = form.email,
                            onValueChange = { update { copy(email = it) } },
                            type = InputType.Email,
                            error = state.errorFor(PersonDraft.EMAIL),
                            optional = true,
                            autocomplete = "off",
                        )
                    }
                    AdminTextField(
                        id = "person-${PersonDraft.PHONE}",
                        label = "Telefon",
                        value = form.phone,
                        onValueChange = { update { copy(phone = it) } },
                        type = InputType.Tel,
                        error = state.errorFor(PersonDraft.PHONE),
                        optional = true,
                        autocomplete = "off",
                    )
                    AdminTextField(
                        id = "person-${PersonDraft.MOBILE_PHONE}",
                        label = "Mobil",
                        value = form.mobilePhone,
                        onValueChange = { update { copy(mobilePhone = it) } },
                        type = InputType.Tel,
                        error = state.errorFor(PersonDraft.MOBILE_PHONE),
                        optional = true,
                        autocomplete = "off",
                    )
                }

                Fieldset({ classes(address.block) }) {
                    Legend({ classes(address.element("legend").className) }) {
                        Text("Adresse ")
                        Span({ classes(address.element("optional").className) }) { Text("(optional)") }
                    }
                    val current = form.address
                    if (current == null) {
                        Div {
                            AdminButton(variant = ButtonVariant.ADD, onClick = { update { copy(address = AddressDraft()) } }) {
                                Text("+ Adresse")
                            }
                        }
                    } else {
                        Div({ classes(formGrid.block, address.element("fields").className) }) {
                            AdminTextField(
                                id = "person-${PersonDraft.STREET}",
                                label = "Straße",
                                value = current.street,
                                onValueChange = { updateAddress { copy(street = it) } },
                                error = state.errorFor(PersonDraft.STREET),
                                autocomplete = "off",
                            )
                            AdminTextField(
                                id = "person-${PersonDraft.HOUSE_NUMBER}",
                                label = "Hausnummer",
                                value = current.houseNumber,
                                onValueChange = { updateAddress { copy(houseNumber = it) } },
                                error = state.errorFor(PersonDraft.HOUSE_NUMBER),
                                autocomplete = "off",
                            )
                            AdminTextField(
                                id = "person-${PersonDraft.ZIPCODE}",
                                label = "PLZ",
                                value = current.zipcode,
                                onValueChange = { updateAddress { copy(zipcode = it) } },
                                error = state.errorFor(PersonDraft.ZIPCODE),
                                inputMode = "numeric",
                                autocomplete = "off",
                            )
                            AdminTextField(
                                id = "person-${PersonDraft.CITY}",
                                label = "Ort",
                                value = current.city,
                                onValueChange = { updateAddress { copy(city = it) } },
                                error = state.errorFor(PersonDraft.CITY),
                                autocomplete = "off",
                            )
                        }
                        Div {
                            AdminButton(variant = ButtonVariant.LINK, onClick = { update { copy(address = null) } }) {
                                Text("Adresse entfernen")
                            }
                        }
                    }
                }
            }

            loaded?.let { RoleHistory(it, onOpenAct) }

            deleteBlocker?.let { blocker -> AdminCallout { Text(blocker) } }
        }
    }
}

/** The photo with buttons to upload, replace and remove it; it is saved with the rest of the form. */
@Composable
private fun PersonPhoto(state: PersonEditorState, api: AdminApi) {
    val photo = state.photo
    val shown = photo.images.lastOrNull()
    val url = shown?.let { it.previewUrl ?: api.imageUrl(ImageCategory.PERSONS, it.uuid!!) }
    val messageId = "person-photo-message"
    val error = state.errorFor(PersonDraft.IMAGE) ?: photo.message
    var editingDetails by remember { mutableStateOf<String?>(null) }

    Div({
        classes(photoBlock.block)
        attr("role", "group")
        attr("aria-label", "Foto")
        if (shown?.uuid == null && shown != null) {
            attr("aria-busy", "true")
        }
    }) {
        AdminAvatar(initials(state.form.firstName, state.form.lastName), url, large = true, alt = if (url != null) "Foto" else "")
        Div({ classes(photoBlock.element("actions").className) }) {
            Label(attrs = { classes("admin-button", "admin-button--link", photoBlock.element("choose").className) }) {
                Text(if (shown == null) "Foto hochladen" else "Foto ändern")
                Input(InputType.File) {
                    classes(photoBlock.element("file-input").className)
                    attr("accept", IMAGE_EXTENSIONS.joinToString(",") { ".$it" })
                    attr("aria-describedby", messageId)
                    onChange { event ->
                        event.target.files?.asList()?.firstOrNull()?.let { photo.add(listOf(it)) }
                        // the same file can be chosen again after removing it
                        event.target.value = ""
                    }
                }
            }
            if (shown != null) {
                AdminButton(variant = ButtonVariant.LINK, onClick = { photo.images.map { it.key }.forEach(photo::remove) }) {
                    Text("Foto entfernen")
                }
            }
            // the image details (#105), once the photo is uploaded
            shown?.uuid?.let { uuid ->
                AdminButton(variant = ButtonVariant.LINK, onClick = { editingDetails = uuid }) { Text("Bilddetails") }
                if (!photo.detailsOf(uuid).hasDescription) {
                    AdminBadge("Beschreibung fehlt", BadgeVariant.ATTENTION)
                }
            }
            Span({
                classes(photoBlock.element(if (error != null) "error" else "hint").className)
                id(messageId)
                attr("role", "status")
            }) {
                Text(
                    when {
                        error != null -> error
                        shown != null && shown.uuid == null -> "Wird hochgeladen …"
                        else -> "Die Website zeigt das Foto auf der Vorstandsseite."
                    },
                )
            }
        }
        AdminImageDetailsDialog(
            open = editingDetails != null,
            id = "person-foto-bilddetails",
            imageUrl = editingDetails?.let { api.imageUrl(ImageCategory.PERSONS, it) },
            details = editingDetails?.let(photo::detailsOf) ?: ImageDetails.NONE,
            onApply = { details ->
                editingDetails?.let { photo.setDetails(it, details) }
                editingDetails = null
            },
            onCancel = { editingDetails = null },
        )
    }
}

/** The productions the person played or worked in, read-only: roles are given in the production's cast. */
@Composable
private fun RoleHistory(loaded: PersonEditor, onOpenAct: (Long) -> Unit) {
    Section({
        classes(roles.block)
        attr("aria-labelledby", "person-rollen")
    }) {
        H3({
            classes(roles.element("title").className)
            id("person-rollen")
        }) { Text("Rollen") }
        if (loaded.roles.isEmpty()) {
            P({ classes(roles.element("hint").className) }) { Text("Noch keine Rollen.") }
        } else {
            Ul({ classes(roles.element("list").className) }) {
                loaded.roles.forEach { role ->
                    Li({ classes(roles.element("item").className) }) {
                        Span { Text(role.roleName) }
                        A(Route.Acts(actId = role.actId).path, {
                            classes(roles.element("act").className)
                            onClick { event ->
                                if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                                    event.preventDefault()
                                    onOpenAct(role.actId)
                                }
                            }
                        }) {
                            Text("${role.actTitle} (${role.actYear})")
                        }
                    }
                }
            }
        }
        P({ classes(roles.element("hint").className) }) { Text("Rollen werden im jeweiligen Stück unter „Besetzung“ vergeben.") }
    }
}
